import React, { InputHTMLAttributes, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useAccount } from 'shared/hooks/use-web3';
import { usePlayerGamesStore } from 'shared/store/playerGamesStore';

import Copy from 'assets/copy2.svg?react';
import Send from 'assets/send.svg?react';

interface Props extends InputHTMLAttributes<HTMLInputElement> {
  type?: string;
  className?: string;
  balance?: number;
  label?: string;
  copy?: boolean;
}

export const InputHash = ({ type = 'text', className, balance, label, ...props }: Props) => {
  const { mainSalt, setMainSalt } = usePlayerGamesStore();
  const { address, chainId } = useAccount();
  const { activeRoomId } = useParams();
  const [isBlinking, setIsBlinking] = useState(false);
  const [bgColor, setBgColor] = useState('#030334');
  const [inputValue, setInputValue] = useState<string>('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (mainSalt[activeRoomId! + address! + chainId]) {
      setInputValue(mainSalt[activeRoomId! + address! + chainId]!);
    }
  }, [mainSalt, activeRoomId, address, chainId]);

  const copyHandler = () => {
    if (inputRef.current) {
      inputRef.current.select();
    }
    navigator.clipboard
      .writeText(inputValue!.toString())
      .then(() => {
        setIsBlinking(true);
        setBgColor('#0000FF');
        setTimeout(() => {
          setIsBlinking(false);
          setBgColor('#030334');
        }, 300);
      })
      .catch((err) => {
        console.error('Failed to copy: ', err);
      });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const saveSalt = () => {
    setMainSalt(inputValue, activeRoomId! + address! + chainId);
  };

  return (
    <div className={`relative w-[100%] opacity-60 ${className ?? ''}`}>
      <input
        ref={inputRef}
        className={`rounded-[7px] border-[1px] border-indigo px-[10px] py-[6px] text-center text-[10px] text-white focus:border-transparent focus:outline-none hd:text-[12px] ${
          type === 'number'
            ? '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none'
            : ''
        } w-[100%] text-white outline-none`}
        type="text"
        value={inputValue ?? ''}
        readOnly={!!mainSalt[activeRoomId! + address! + chainId]}
        onChange={handleChange}
        style={{
          backgroundColor: bgColor,
          transition: 'background-color 0.3s ease',
        }}
        {...props}
      />

      {mainSalt[activeRoomId! + address! + chainId] ? (
        <Copy
          className={`pointer absolute right-2 top-[50%] -translate-y-[50%] ${
            isBlinking ? 'animate-blink' : ''
          }`}
          onClick={copyHandler}
          data-testid="copy-icon"
        />
      ) : (
        <Send
          className={`pointer absolute right-2 top-[50%] h-4 -translate-y-[50%] ${
            isBlinking ? 'animate-blink' : ''
          }`}
          onClick={saveSalt}
          data-testid="send-icon"
        />
      )}
    </div>
  );
};
